import type { ConversionPlaybook } from '@/lib/commercial/conversionPlaybookTypes';

interface Props {
  playbook: ConversionPlaybook;
  className?: string;
}

function CopyBtn({ text }: { text: string }) {
  return (
    <button
      type="button"
      onClick={() => navigator.clipboard.writeText(text).catch(() => {})}
      className="inline-flex items-center gap-0.5 text-[11px] text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
    >
      <i className="ri-file-copy-line"></i>
      复制
    </button>
  );
}

function Block({
  title,
  placement,
  timing,
  copy,
  next,
}: {
  title: string;
  placement?: string;
  timing?: string;
  copy: string;
  next?: string;
}) {
  return (
    <div className="rounded-lg border border-secondary-100 bg-background-50/80 p-3 space-y-1.5">
      <div className="text-[11px] font-semibold text-secondary-500">{title}</div>
      {placement && (
        <div className="text-[11px] text-foreground-600">
          <span className="text-secondary-400">使用位置：</span>
          {placement}
        </div>
      )}
      {timing && (
        <div className="text-[11px] text-foreground-600">
          <span className="text-secondary-400">使用时机：</span>
          {timing}
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm text-foreground-800 leading-relaxed whitespace-pre-line flex-1">{copy}</p>
        <CopyBtn text={copy} />
      </div>
      {next && (
        <div className="text-[11px] text-foreground-600">
          <span className="text-secondary-400">下一步：</span>
          {next}
        </div>
      )}
    </div>
  );
}

/**
 * 【今日客群开发 SOP】
 * 只展示可执行步骤与可复制话术，不暴露工程字段。
 */
export default function ConversionPlaybookCard({ playbook, className = '' }: Props) {
  const p = playbook;
  return (
    <section
      className={`rounded-xl border border-accent-200 bg-gradient-to-br from-accent-50/70 via-background-50 to-background-50 p-4 md:p-5 space-y-3 ${className}`}
    >
      <div className="flex items-center gap-2">
        <i className="ri-route-line text-accent-600 text-lg"></i>
        <h2 className="text-sm md:text-base font-semibold text-foreground-950">
          今日客群开发 SOP
        </h2>
      </div>

      <p className="text-[11px] text-secondary-600 leading-relaxed rounded-md bg-secondary-50 border border-secondary-100 px-2.5 py-2">
        先依今日 SOP 使用现有素材，不需要为了找不到用法立即重新生成。你仍可自行调整措辞。
      </p>

      <div className="space-y-2.5">
        <Block
          title="1. 作品正文"
          placement={p.public_post.placement}
          timing="发布作品时"
          copy={p.public_post.copy}
          next={p.public_post.purpose}
        />

        <Block
          title="2. 置顶留言"
          placement={p.pinned_comment.placement}
          timing="作品发布后立刻"
          copy={p.pinned_comment.copy}
          next={p.pinned_comment.purpose}
        />

        <div className="rounded-lg border border-secondary-100 bg-background-50/80 p-3 space-y-2">
          <div className="text-[11px] font-semibold text-secondary-500">3. 公开留言怎么回</div>
          <div className="text-[11px] text-foreground-600">
            <span className="text-secondary-400">使用位置：</span>
            作品下方公开留言区
          </div>
          {p.public_replies.map((r, i) => (
            <div key={i} className="border-t border-secondary-100 pt-2 space-y-1">
              <div className="text-[11px] font-medium text-foreground-700">情况：{r.situation}</div>
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm text-foreground-800 leading-relaxed flex-1">{r.copy}</p>
                <CopyBtn text={r.copy} />
              </div>
              <div className="text-[11px] text-foreground-600">
                <span className="text-secondary-400">下一步：</span>
                {r.next_step}
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-lg border border-secondary-100 bg-background-50/80 p-3 space-y-2">
          <div className="text-[11px] font-semibold text-secondary-500">4. 什么时候进聊天区域</div>
          <div className="text-[11px] text-foreground-600">
            <span className="text-secondary-400">使用时机：</span>
            仅在下列情况出现时
          </div>
          <ul className="space-y-1.5 text-sm text-foreground-800">
            {p.chat_openers.map((c, i) => (
              <li key={i}>· {c.trigger}</li>
            ))}
          </ul>
          <div className="text-[11px] text-foreground-600">
            <span className="text-secondary-400">下一步：</span>
            进入聊天后先用「私聊第一句」，不要直接丢联络方式
          </div>
        </div>

        <div className="rounded-lg border border-secondary-100 bg-background-50/80 p-3 space-y-2">
          <div className="text-[11px] font-semibold text-secondary-500">5. 私聊第一句</div>
          <div className="text-[11px] text-foreground-600">
            <span className="text-secondary-400">使用位置：</span>
            私信 / 聊天区域
          </div>
          {p.chat_openers.map((c, i) => (
            <div key={i} className="border-t border-secondary-100 pt-2 space-y-1">
              <div className="text-[11px] font-medium text-foreground-700">时机：{c.trigger}</div>
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm text-foreground-800 leading-relaxed flex-1">{c.copy}</p>
                <CopyBtn text={c.copy} />
              </div>
              <div className="text-[11px] text-foreground-600">
                <span className="text-secondary-400">下一步：</span>
                {c.purpose}
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-lg border border-secondary-100 bg-background-50/80 p-3 space-y-1.5">
          <div className="text-[11px] font-semibold text-secondary-500">6. 先问哪几个问题</div>
          <div className="text-[11px] text-foreground-600">
            <span className="text-secondary-400">使用时机：</span>
            私聊了解需求时（一次问一题）
          </div>
          <ul className="space-y-1">
            {p.qualification_questions.map((q, i) => (
              <li key={i} className="flex items-start justify-between gap-2 text-sm text-foreground-800">
                <span>
                  {i + 1}. {q}
                </span>
                <CopyBtn text={q} />
              </li>
            ))}
          </ul>
          <div className="text-[11px] text-foreground-600">
            <span className="text-secondary-400">下一步：</span>
            听完对方回答再决定是否进入「有兴趣怎么接」
          </div>
        </div>

        <Block
          title="7. 对方有兴趣怎么接"
          placement="私信"
          timing="对方已明确表示有兴趣之后"
          copy={p.interested_response}
          next="先确认需求；不要强迫成交，不要制造假稀缺"
        />

        <Block
          title="8. 什么时候提供后续承接方式"
          placement="仅私聊（不得放在公开贴文）"
          timing={p.destination_handoff.condition}
          copy={`${p.destination_handoff.destination_label}\n${p.destination_handoff.copy}`}
          next="未设定承接方式时，先去完成设定；已设定则等对方明确有兴趣且聊过一轮后再提供"
        />

        <Block
          title="9. 对方拒绝怎么收尾"
          placement="公开留言或私信"
          timing="对方明确拒绝或不感兴趣时"
          copy={p.refusal_response}
          next="立刻停止；不追问、不换方式继续推"
        />

        <Block
          title="10. 没回覆怎么处理"
          placement="私信"
          timing={`等待 ${p.no_reply_followup.wait_hours} 小时后，最多提醒 ${p.no_reply_followup.max_followups} 次`}
          copy={p.no_reply_followup.copy}
          next="第二次仍无回覆即停止，不再发送"
        />

        <div className="rounded-lg border border-secondary-100 bg-background-50/80 p-3 space-y-1.5">
          <div className="text-[11px] font-semibold text-secondary-500">11. 今日操作步骤</div>
          <ol className="space-y-1 text-sm text-foreground-800 list-decimal list-inside">
            {p.daily_sop.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        </div>

        <div className="rounded-lg border border-amber-100 bg-amber-50/50 p-3 space-y-1.5">
          <div className="text-[11px] font-semibold text-amber-700">12. 安全提醒</div>
          <ul className="space-y-0.5 text-sm text-foreground-800">
            {p.safety_notes.map((n, i) => (
              <li key={i}>· {n}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
